import {
  AppPageHeader,
  DeleteIcon,
  EditIcon,
  InfiniteTable,
} from '@/components/_common';
import {
  useForecastPayableDelete,
  useForecastPayableList,
} from '@/core/domain/financial/ForecastPayable/ForecastPayable.hooks';
import {
  ForecastPayable,
  ForecastPayableColumnConfig,
  ForecastPayableFieldOption,
  ListForecastPayableQuery,
} from '@/core/domain/financial/ForecastPayable/ForecastPayable.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { ColumnsType } from '@/components/_common/InfiniteTable';
import { Button, Divider, Modal, Space } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { tableSorterOrder } from '@/utils/table';
import ForecastPayableExtraFilters from '../ForecastPayableExtraFilters';
import { useDebounce } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

const ForcastPayableList: React.FC = () => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListForecastPayableQuery>({
    order: 'DESC',
    ...convertParams<ListForecastPayableQuery>({
      search: 'string',
      companyId: 'number',
      situation: 'string',
      description: 'string',
      documentNumber: 'string',
      documentTypeId: 'number',
      payDay: 'number',
      planAccountId: 'string',
      creditor: 'string',
      value: 'number',
      orderColumns: 'string',
      order: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useForecastPayableList(queryDebounce);

  const forecastPayableList = flatInfiniteData(data);

  const removeMutation = useForecastPayableDelete();

  function handleOrderForecastPayable(sorter: SorterResult<ForecastPayable>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderColumns === ForecastPayableColumnConfig[sorter.columnKey]
          ) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: ForecastPayableColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Remoção da previsão',
      content:
        'Deseja realmente remover a previsão, essa ação será irreversível',
      onOk: async () => {
        await removeMutation.mutateAsync(id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<ForecastPayable> = [
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'predictions.list.header.companies',
      }),
      key: ForecastPayableFieldOption.company,
    },
    {
      title: intl.formatMessage({
        id: 'predictions.list.header.creditor',
      }),
      key: ForecastPayableFieldOption.creditor,
      render: (_, row) => {
        if (!!!row.employee.id) {
          return row.supplier.name;
        } else {
          return row.employee.name;
        }
      },
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'predictions.list.header.description',
      }),
      key: ForecastPayableFieldOption.description,
    },
    {
      width: 100,
      align: 'right',
      fixed: 'right',
      title: intl.formatMessage({ id: 'predictions.list.header.actions' }),
      render: (_, row) => (
        <Space>
          <EditIcon
            redirect={`/app/financial/billToPay/forecastPayable/${row.id}`}
            disabled={
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT_FORECAST_OF_ACCOUNTS_PAYABLE,
              ])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_PAYABLE_DELETE_FORECAST_OF_ACCOUNTS_PAYABLE,
              ])
            }
            onClick={() => handleDelete(row.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <ForecastPayableExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />
      <InfiniteTable<ForecastPayable>
        columns={columns}
        dataSource={forecastPayableList}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrderForecastPayable(sorter);
          }
        }}
      />
    </>
  );
};

export default ForcastPayableList;
